import React, { useId } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Link } from 'react-router-dom';
import { APP_SHELL_DIMENSIONS, getAppSidebarWidth } from '../../styles/appShellStyles';

function BrandMark() {
  return (
    <Box
      component="svg"
      viewBox="0 0 32 32"
      aria-hidden="true"
      sx={{ width: 26, height: 26, flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="15" fill="#2684ff" />
      <path d="M7 23 15.8 6 25 24l-9.1-6.7L7 23Z" fill="#071c31" />
      <path d="m15.8 6 1 11.3L25 24 15.8 6Z" fill="#bfe0ff" opacity=".9" />
    </Box>
  );
}

const ADMIN_ONLY_LABEL = 'System admin only';

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function NavigationBody({
  collapsed,
  navItems,
  pathname,
  setupGuideTargetPath,
  setupGuideTargetRef = null,
  onSettings,
  settingsActionRef = null,
  onNavigate,
  onCollapsedChange,
  isProduction,
  appMode,
  appearanceStyles,
  adminAccentColor = 'warning.main',
}) {
  const descriptionIdPrefix = useId();
  const renderItem = ({ label, path, icon: Icon, adminOnly = false }) => {
    const active = pathname === path;
    const adminDescriptionId = adminOnly ? `${descriptionIdPrefix}${path}` : undefined;
    return (
      <Box key={path} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Tooltip title={collapsed ? label : ''} placement="right">
          <ButtonBase
            ref={setupGuideTargetPath === path ? setupGuideTargetRef : null}
            component={Link}
            to={path}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            aria-describedby={adminDescriptionId}
            sx={{
              border: '1px solid transparent',
              ...appearanceStyles.navigationItem(active),
              minHeight: 40,
              flex: 1,
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 1,
              px: collapsed ? 0.7 : 1.05,
              transition: 'background-color 140ms ease, color 140ms ease',
            }}
          >
            <Box
              component="span"
              sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
            >
              <Icon sx={{ fontSize: 21 }} />
              {collapsed && adminOnly && (
                <Box
                  component="span"
                  aria-hidden="true"
                  data-testid="nav-admin-only-badge"
                  sx={{
                    position: 'absolute',
                    right: -7,
                    bottom: -6,
                    display: 'inline-flex',
                    color: adminAccentColor,
                    lineHeight: 0,
                  }}
                >
                  <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 12 }} />
                </Box>
              )}
            </Box>
            {!collapsed && (
              <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                {label}
              </Typography>
            )}
            {!collapsed && adminOnly && (
              <Tooltip title={ADMIN_ONLY_LABEL} placement="right" describeChild>
                <Box
                  component="span"
                  aria-hidden="true"
                  data-testid="nav-admin-only-badge"
                  sx={{ display: 'inline-flex', flexShrink: 0, ml: -0.4, color: adminAccentColor, lineHeight: 0 }}
                >
                  <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            )}
          </ButtonBase>
        </Tooltip>
        {adminOnly && (
          <Box component="span" id={adminDescriptionId} sx={visuallyHiddenSx}>
            {ADMIN_ONLY_LABEL}
          </Box>
        )}
      </Box>
    );
  };

  const renderAction = (label, Icon, handler, actionRef = null) => (
    <Tooltip title={collapsed ? label : ''} placement="right">
      <ButtonBase
        ref={actionRef}
        onClick={() => {
          handler?.();
          onNavigate?.();
        }}
        sx={{
          ...appearanceStyles.navigationItem(false),
          minHeight: 40,
          width: '100%',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1,
          px: collapsed ? 0.7 : 1.05,
          border: '1px solid transparent',
        }}
      >
        <Icon sx={{ fontSize: 21 }} />
        {!collapsed && <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{label}</Typography>}
      </ButtonBase>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: collapsed ? 0.5 : 0.75,
        py: 1.2,
        color: appearanceStyles.sidebar.color,
      }}
    >
      <Box sx={{ minHeight: 38, display: 'flex', alignItems: 'center', gap: 0.7, px: collapsed ? 0.55 : 0.45 }}>
        <BrandMark />
        {!collapsed && (
          <>
            <Typography sx={{ ...appearanceStyles.wordmark, fontSize: '0.78rem', letterSpacing: '0.02em' }}>
              ALPHAMIZER
            </Typography>
            {!isProduction && (
              <Box
                component="span"
                sx={{
                  ...appearanceStyles.environmentBadge,
                  ml: 'auto',
                  px: 0.5,
                  py: 0.15,
                  borderRadius: 999,
                  border: '1px solid',
                  fontSize: '0.52rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                {appMode || 'dev'}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box component="nav" aria-label="Primary navigation" sx={{ display: 'grid', gap: 0.45, mt: 1.6 }}>
        {navItems.map(renderItem)}
      </Box>

      <Divider sx={{ ...appearanceStyles.sidebarDivider, my: 1.6 }} />
      <Box sx={{ display: 'grid', gap: 0.55 }}>
        {renderAction('Settings', SettingsOutlinedIcon, onSettings, settingsActionRef)}
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <ButtonBase
          onClick={() => onCollapsedChange?.(!collapsed)}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          sx={{
            ...appearanceStyles.navigationItem(false),
            minHeight: 40,
            width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 0.8,
            px: collapsed ? 0.7 : 1.05,
            border: '1px solid transparent',
          }}
        >
          {collapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
          {!collapsed && <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Collapse</Typography>}
        </ButtonBase>
      </Box>
    </Box>
  );
}

export default function AppNavigationSidebar({
  collapsed,
  mobileOpen,
  onMobileClose,
  appearanceStyles,
  settingsActionRef = null,
  setupGuideTargetRef = null,
  ...bodyProps
}) {
  const width = getAppSidebarWidth(collapsed);
  // Only the desktop rail exposes its Settings action and guided tab: the
  // mobile drawer's copy stays mounted while hidden, so it cannot anchor a guide.
  const body = (
    <NavigationBody
      collapsed={collapsed}
      appearanceStyles={appearanceStyles}
      settingsActionRef={settingsActionRef}
      setupGuideTargetRef={setupGuideTargetRef}
      {...bodyProps}
    />
  );

  return (
    <>
      <Box
        component="aside"
        data-testid="app-navigation-sidebar"
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          inset: '0 auto 0 0',
          zIndex: (theme) => theme.zIndex.appBar + 1,
          width,
          ...appearanceStyles.sidebar,
          borderRight: '1px solid',
          borderRightColor: appearanceStyles.sidebar.borderColor,
          transition: (theme) => theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {body}
      </Box>
      <Drawer
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{
          sx: {
            width: APP_SHELL_DIMENSIONS.sidebarWidth,
            ...appearanceStyles.sidebar,
          },
        }}
      >
        <NavigationBody
          collapsed={false}
          appearanceStyles={appearanceStyles}
          {...bodyProps}
          onNavigate={onMobileClose}
        />
      </Drawer>
    </>
  );
}

const navItemType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  // System-admin-only destinations carry a small admin shield after the label.
  adminOnly: PropTypes.bool,
});

NavigationBody.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  navItems: PropTypes.arrayOf(navItemType).isRequired,
  pathname: PropTypes.string.isRequired,
  setupGuideTargetPath: PropTypes.string,
  setupGuideTargetRef: PropTypes.func,
  onSettings: PropTypes.func,
  settingsActionRef: PropTypes.func,
  onNavigate: PropTypes.func,
  onCollapsedChange: PropTypes.func,
  isProduction: PropTypes.bool.isRequired,
  appMode: PropTypes.string,
  appearanceStyles: PropTypes.object.isRequired,
  adminAccentColor: PropTypes.string,
};

AppNavigationSidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  mobileOpen: PropTypes.bool.isRequired,
  onMobileClose: PropTypes.func.isRequired,
  navItems: PropTypes.arrayOf(navItemType).isRequired,
  pathname: PropTypes.string.isRequired,
  setupGuideTargetPath: PropTypes.string,
  setupGuideTargetRef: PropTypes.func,
  onSettings: PropTypes.func,
  settingsActionRef: PropTypes.func,
  onCollapsedChange: PropTypes.func,
  isProduction: PropTypes.bool.isRequired,
  appMode: PropTypes.string,
  appearanceStyles: PropTypes.object.isRequired,
  adminAccentColor: PropTypes.string,
};
